import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Element } from "react-scroll";
import { Code, Zap, Users, Target } from "lucide-react";
import { useTranslation } from "react-i18next";
// @ts-ignore
import me from "../assets/me.jpg";

const About: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const skills = [
    {
      icon: Code,
      title: t("about.skills.fullStack.title"),
      description: t("about.skills.fullStack.description"),
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: Zap,
      title: t("about.skills.performance.title"),
      description: t("about.skills.performance.description"),
      color: "from-yellow-500 to-orange-500",
    },
    {
      icon: Users,
      title: t("about.skills.collaboration.title"),
      description: t("about.skills.collaboration.description"),
      color: "from-green-500 to-teal-500",
    },
    {
      icon: Target,
      title: t("about.skills.problemSolving.title"),
      description: t("about.skills.problemSolving.description"),
      color: "from-purple-500 to-pink-500",
    },
  ];

  return (
    <Element name="about">
      <section ref={ref} className="py-20 lg:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent" />

        <div className="relative z-10 section-padding container-width">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="gradient-text">{t("about.title")}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                {t("about.subtitle")}
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-20">
              <div className="glass-card p-8 lg:p-12 rounded-2xl">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                      {t("about.aboutMeTitle")}
                    </h3>
                    <div className="space-y-4 text-gray-300 leading-relaxed">
                      <p>{t("about.description1")}</p>
                      <p>{t("about.description2")}</p>
                      <p>{t("about.description3")}</p>
                    </div>
                  </div>
                  <div className="relative">
                    <motion.div
                      className="w-full aspect-square bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      <img className="rounded-xl" src={me} />
                    </motion.div>

                    <motion.div
                      className="absolute -top-4 -right-4 w-8 h-8 bg-blue-400 rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <motion.div
                      className="absolute -bottom-2 -left-4 w-6 h-6 bg-purple-400 rounded-full"
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <h3 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
                {t("about.whatIDoTitle")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill.title}
                    variants={itemVariants}
                    custom={index}
                    className="glass-card p-6 rounded-xl hover:bg-white/20 transition-all duration-300 group"
                    whileHover={{
                      scale: 1.05,
                      transition: {
                        type: "spring",
                        stiffness: 400,
                        damping: 17,
                      },
                    }}
                  >
                    <div
                      className={`w-12 h-12 bg-gradient-to-r ${skill.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <skill.icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors">
                      {skill.title}
                    </h4>
                    <p className="text-gray-400 leading-relaxed">
                      {skill.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Element>
  );
};

export default About;
